import React, { useCallback, useContext } from "react";
import SinglePage from "../../common/panels/SinglePage/SinglePage";
import { SessionContext } from "../../common/contexts/SessionContext";
import { PageNotAllowed } from "../../main/common/PageNotAllowed/PageNotAllowed";
import { useHistory } from "react-router-dom";
import { getAddRoute, getEditLink } from "../../main/Routing";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { usePost } from "../../api/BaseEntityApi";
import { zontalCredentialsConstants, ZontalCredentialsReadModel } from "../../api/Zontal";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { Alert } from "../../common/overlays/Alert/Alert";
import { Tile } from "../../common/panels/Tile/Tile";
import { ContentTable } from "../../common/tables/ContentTable/ContentTable";
import { DateTimeRenderer } from "../../common/datetime/DateTimeFormatter";
import { useEntityApi } from "../../api/useEntityApi";
import { AlertModal } from "../../common/modals/AlertModal/AlertModal";
import { GetPersons } from "../../common/misc/EntityRenders/EntityRenderer";

export const ZontalIntegrationLandingPage = () => {
  const history = useHistory();
  const { session, route } = useContext(SessionContext);

  const {
    data: zontalCredentials,
    status,
    fetchStatus,
    error,
    refetch,
  } = usePost<ZontalCredentialsReadModel[]>("zontal/credentials/list", {});

  const { deleteMutationAsync, isLoadingDeleteMutation } =
    useEntityApi<ZontalCredentialsReadModel>("zontal/credentials");

  const deleteCredential = useCallback(
    async (id: number) => {
      await deleteMutationAsync({ id }).catch(() => {});
      await refetch();
    },
    [deleteMutationAsync, refetch]
  );

  if ([!!session?.permissions.view_admin_pages, (!!session?.features.enable_zontal_integration || !!session?.features.enable_zontal_automatic_data_push)].some((p) => p === false))
    return <PageNotAllowed />;
  return (
    <SinglePage>
      <SinglePage.Head>
        <SinglePage.Head.Label>
          <div className="flex row-nowrap align-center gap-5">
            <img
              src={`${process.env.PUBLIC_URL}/assets/logos/favicon_zontal.svg`}
              className="feather"
              alt="ZONTAL"
              style={{ width: 24, height: 24, marginRight: 8 }}
            />
            ZONTAL Integration
          </div>
        </SinglePage.Head.Label>
      </SinglePage.Head>
      <SinglePage.Content>
        <div
          className="flex col-nowrap gap-5"
          style={{
            padding: "24px",
            borderRadius: "8px",
            marginBottom: "16px",
          }}
        >
          <h2 style={{ margin: "0 0 12px 0", color: "#2d3a4b" }}>Welcome to the ZONTAL Integration</h2>
          <p style={{ margin: 0, color: "#4a5a6a", fontSize: "1.1em" }}>
            Here you can manage and configure your <b>ZONTAL</b> integration settings.
          </p>
          <hr />
          <h3>Your ZONTAL connections</h3>
          <LoadingWrapper status={status} fetchStatus={fetchStatus} error={error}>
            {zontalCredentials && zontalCredentials.length > 0 ? (
              zontalCredentials.map((credential, index) => (
                <ZontalCredentialTile
                  key={index}
                  credential={credential}
                  deleteCredential={deleteCredential}
                  isLoadingDeleteMutation={isLoadingDeleteMutation}
                />
              ))
            ) : (
              <Alert
                type="danger"
                message="No ZONTAL connections are configured. Please set up your credentials to ZONTAL."
                fit
              />
            )}
          </LoadingWrapper>
          {Array.isArray(zontalCredentials) && !zontalCredentials.length && (
            <button
              className="btn btn-block btn-primary"
              onClick={() => {
                history.push(route(getAddRoute(zontalCredentialsConstants.frontendIndexRoute)));
              }}
              title="Add ZONTAL Credentials"
            >
              <LucideIcon name="plus" /> Add ZONTAL Credentials
            </button>
          )}
        </div>
        {/* Additional content and components related to ZONTAL integration can be added here */}
      </SinglePage.Content>
    </SinglePage>
  );
};

const ZontalCredentialTile = ({
  credential,
  deleteCredential,
  isLoadingDeleteMutation,
}: {
  credential: ZontalCredentialsReadModel;
  deleteCredential: (id: number) => Promise<void>;
  isLoadingDeleteMutation: boolean;
}) => {
  const { route } = useContext(SessionContext);
  const [showAlertModal, setShowAlertModal] = React.useState(false);
  const history = useHistory();
  return (
    <Tile foldable isFolded={true}>
      <Tile.Head title={credential.host}>
        <Tile.Head.Controls>
          <Tile.Head.Controls.Fixed>
            <button
              className="btn btn-sm btn-ghost-secondary"
              title="Edit ZONTAL Credential"
              onClick={() =>
                history.push(route(getEditLink(zontalCredentialsConstants.frontendIndexRoute, credential.id)))
              }
            >
              <LucideIcon name="pencil" />
            </button>

            <AlertModal
              type="danger"
              title="Delete ZONTAL Credential"
              description="This action cannot be undone. Access via this ZONTAL credential will no longer be possible."
              showModal={showAlertModal}
              setShowModal={setShowAlertModal}
              onProceed={async () => {
                await deleteCredential(credential.id);
                setShowAlertModal(false);
              }}
              loading={isLoadingDeleteMutation}
            />
            <button
              className="btn btn-sm btn-ghost-danger"
              title="Delete ZONTAL Credential"
              onClick={() => setShowAlertModal(true)}
            >
              <LucideIcon name="trash-2" />
            </button>
          </Tile.Head.Controls.Fixed>
        </Tile.Head.Controls>
      </Tile.Head>
      <Tile.Body>
        <ContentTable
          content={[
            {
              key: "Username",
              value: credential.username,
            },
            {
              key: "Host",
              value: <pre style={{ width: "100%" }}>{credential.host}</pre>,
            },
            ...(credential.clientId
              ? [
                  {
                    key: "Client ID",
                    value: credential.clientId,
                  },
                ]
              : []),
            {
              key: "Created On",
              value: <DateTimeRenderer date={credential.enteredOn} />,
            },
            {
              key: "Created by",
              value: <GetPersons style={{ gap: 2.5 }} persons={[credential.enteredBy]} />,
            },
            {
              key: "Last modified on",
              value: <DateTimeRenderer date={credential.modifiedOn} />,
            },
            {
              key: "Last modified by",
              value: <GetPersons style={{ gap: 2.5 }} persons={[credential.modifiedBy]} />,
            },
          ]}
        />
      </Tile.Body>
    </Tile>
  );
};
